import React, { useMemo } from "react";
import Card, { cx } from "../../features/ui/Card";
import ProOverlay from "./ProOverlay";
import Spinner from "./Spinner";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function AnalysisCard({
  moduleStats,
  locked = false,
  lockText = "Upgrade to Pro to unlock this feature.",
  onRequestPro,
  analysisLoading = false,
}) {
  const { buckets, maxCount } = useMemo(() => {
    const grades = (moduleStats?.modules || [])
      .map((m) => Number(m?.grade))
      .filter((g) => Number.isFinite(g));

    const defs = [
      { key: "30s", label: "30–39", min: 30, max: 40 },
      { key: "40s", label: "40–49", min: 40, max: 50 },
      { key: "50s", label: "50–59", min: 50, max: 60 },
      { key: "60s", label: "60–69", min: 60, max: 70 },
      { key: "70s", label: "70–79", min: 70, max: 80 },
      { key: "80s", label: "80–89", min: 80, max: 90 },
      { key: "90p", label: "90+", min: 90, max: Infinity },
    ];

    const counts = defs.map((d) => ({ ...d, count: 0 }));

    for (const g of grades) {
      const grade = clamp(g, 0, 100);
      const bucket = counts.find((b) => grade >= b.min && grade < b.max);
      if (bucket) bucket.count += 1;
    }

    const max = counts.reduce((m, b) => Math.max(m, b.count), 0);
    return { buckets: counts, maxCount: max };
  }, [moduleStats]);

  return (
    <Card className="relative p-6">
      {/* Loading overlay (relative to this card) */}
      {analysisLoading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/60 dark:bg-neutral-950/60">
          <div className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-700 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200">
            <Spinner size={16} />
            Analysing…
          </div>
        </div>
      ) : null}

      <div className="flex items-start justify-between gap-4">
        <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">
          Grade Distribution Analysis
        </h3>
      </div>

      <div
        className={
          locked
            ? "mt-4 select-none blur-[2px] opacity-60 pointer-events-none"
            : "mt-4"
        }
      >
        <div className={analysisLoading ? "opacity-70" : ""}>
          <div className="rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-950">
            {maxCount === 0 ? (
              <div className="text-sm text-neutral-600 dark:text-neutral-400">
                Enter grades to see the distribution.
              </div>
            ) : (
              <>
                <div className="flex h-24 items-end gap-2">
                  {buckets.map((b) => {
                    const ratio = maxCount > 0 ? b.count / maxCount : 0;
                    const heightPx = b.count === 0 ? 10 : Math.round(12 + ratio * 84);

                    return (
                      <div key={b.key} className="flex w-full flex-col items-stretch">
                        <div
                          className={cx(
                            "relative flex w-full items-center justify-center rounded-md",
                            b.count === 0
                              ? "bg-blue-100 dark:bg-blue-900/30"
                              : "bg-blue-500 dark:bg-blue-600"
                          )}
                          style={{ height: `${heightPx}px` }}
                          title={`${b.label}: ${b.count}`}
                        >
                          <span
                            className={cx(
                              "pointer-events-none select-none text-[12px] font-semibold",
                              b.count === 0
                                ? "text-blue-700 dark:text-blue-300"
                                : "text-white"
                            )}
                          >
                            {b.count}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-2 flex items-center gap-2">
                  {buckets.map((b) => (
                    <div
                      key={b.key}
                      className="w-full text-center text-xs text-neutral-600 dark:text-neutral-400"
                    >
                      {b.label}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {locked ? <ProOverlay text={lockText} onClick={onRequestPro} /> : null}
    </Card>
  );
}